import _ from 'lodash';
import { saveAs } from 'file-saver';
import Data, { postExternalApi, uuid, capitalize } from 'app-core/Data';
import { CREATIVE_VERSION } from 'view/creative/updater';
import ssmlHelper from 'view/insertion/ssml-helper';
import axios from 'axios';
import { findMedia, downloadMedia } from './media-helper';
import audioHelper from 'view/audioTracker/audio-helper';
import auth from 'auth';
import { cut, bufferToWave } from 'app-core/audio';

if (__DEBUG__) {
	window.axios1 = axios;
	window.audioHelper1 = audioHelper;
}

const questionTypes = ['logical', 'list', 'text', 'date', 'time', 'number', 'currency', 'range', 'file', 'language', 'email', 'audioToText', 'radioGroup', 'info', 'websiteUrl', 'productName', 'tabs'];

const START_DELAY = 1000;
const SILENCE_TRIM = -50;

const audioCtx = new AudioContext();

function encloseString(str) {
	str = (str || '')
		.replace(/\\/g, '\\\'5c');

	let unicodeStr = '';
	[...(str || '')].map((f, i) => {
		unicodeStr += `\\u${str.charCodeAt(i)}?`;
	});
	return `${unicodeStr || ''}`;
}

function getFormedResultsRTF(results, save) {
	if (!results) return;
	let text = '';
	results.map(s => {
		text += `\\fs38 ${`${encloseString(s.step.Name)}`.replace(/[\t\r\n]/g, '')}\\fs24\\par\n\\par\n`;
		s.questions.map(q => 
			text += `\\fs28\\b ${`${encloseString(q.Question)}\\b0`.replace(/[\t\r\n]/g, '')}\\fs24\\par ${encloseString(q.Answer) || '-'}\\par\n\\par\n`
		);
	});

	if (save) {
		const rtf = `{\\rtf1\\ansi\\ansicpg1252\\deff0\\deflang2057{\\fonttbl{\\f0\\fnil\\fcharset0 Segoe UI Light;}}\n\\viewkind4\\uc1\\pard\\f0\\fs17 ${text}\\par\n}`;
		const blob =  new Blob([rtf], {
			type: 'application/rtf'
		})
		saveAs(blob, 'brief.rtf');
	}

	return text;
}

async function calcDuration(src) {
	if (__DEBUG__) {
		console.debug('Calc duration', { src });
	}

	const blob = (await axios.get(src, { responseType: 'blob' }))?.data;
	const buffer = await Data.getAudioBufferFromBlob(blob, audioCtx);

	if (__DEBUG__)
		console.debug('Duration calculated', buffer?.duration);

	return buffer?.duration;
}

async function generateSSMLInsertion(insertion) {
	const preparedSsml = ssmlHelper.prepareSsml(insertion.Ssml, 
	{
		rate: insertion.Rate,
		language: insertion.Language || 'en-US',
		voice: insertion.Voice,
		isPreview: true
	});

	insertion.JsonConfig.hasDefaultVoiceReplaced = preparedSsml.hasDefaultVoiceReplaced;

	if (insertion.JsonConfig.hasDefaultVoiceReplaced) {
		insertion.JsonConfig.defaultVoiceName = preparedSsml.voice;
	}

	let response = await postExternalApi('Creative/BuildAudioCustom', {
		...insertion,
		Voice: preparedSsml.voice,
		Ssml: preparedSsml.ssml,
		ClientProcessId: `${auth.getGlobalProcessId('brief')}_brf_hlp_genSSML`
	}).catch(() => {});

	let base64 = response?.AudioData && `data:audio/wav;base64,${response?.AudioData}`;

	return {
		response,
		base64
	}
}

async function generateSSML(insertion, opts) {
	const ssmlResponse = await generateSSMLInsertion(insertion);
	const res = {
		base64: ssmlResponse.base64
	}
	if (opts?.calcDuration && res.base64) {
		res.duration = await calcDuration(res.base64);
	}
	return res;
}

function processAddToDucker(opts, trackNumber) {
	if (!opts?.aggregatedTracks) return;

	opts.aggregatedTracks.map(f => {
		const ducker = f.wapiPlugins && f.wapiPlugins.find(p => p.id === 'duckerV2');
		if (ducker && !ducker.sidechainTrackNumbers.includes(trackNumber)) {
			ducker.sidechainTrackNumbers.push(trackNumber);
		}
	});
}

function processRemoveFromDucker(opts, trackNumber) {
	if (!opts?.aggregatedTracks) return;

	opts.aggregatedTracks.map(f => {
		const ducker = f.wapiPlugins && f.wapiPlugins.find(p => p.id === 'duckerV2');
		if (ducker && ducker.sidechainTrackNumbers.includes(trackNumber)) {
			Data.toggleSelectedIdInArray(trackNumber, ducker.sidechainTrackNumbers);
			if (__DEBUG__)
				console.debug('Removed from ducker', { trackNumber });
		}
	});
}

function findTrackNumber(opts, trackNames, trackTemplates, config) {
	let num = null;
	(trackNames || []).map(f => {
		if (num === null) {
			const track = opts?.aggregatedTracks?.find(t => `${t.name}`.toLowerCase() === `${f}`.toLowerCase());
			if (track) {
				num = track.trackNumber;
			}
		}
	});

	if (trackTemplates?.length && trackNames?.length && num === null) {
		const trackTemplate = (trackTemplates || []).map(f => opts?.aggregatedTracks?.find(t => `${t.name}`.toLowerCase() === `${f}`.toLowerCase())).find(f => f);
		if (__DEBUG__)
			console.debug('Find track numbe: find track from template', { trackTemplate });
		
		const nextTrackNumber = (_.max(opts?.aggregatedTracks?.map(f => f.trackNumber || 0)) || 0) + 1;
		const newAggTrack = {
			..._.cloneDeep(trackTemplate || {}),
			name: capitalize(trackNames[0]),
			trackNumber: nextTrackNumber
		}
		opts.aggregatedTracks.push(newAggTrack);
		num = nextTrackNumber;
		if (__DEBUG__)
			console.debug('Find track number: new track from template', { trackTemplate, nextTrackNumber, newAggTrack });
	}

	if (__DEBUG__)
		console.debug('Find track number', { opts, trackNames, trackTemplates });

	if (config?.addToDucker && num != null) {
		processAddToDucker(opts, num);
	}

	return num || 0;
}

function preparePhraseText(f) {
	let text = f?.text || '';
	if (f.phonemes?.length) {
		f.phonemes.map(ph => {
			var phonemeSsml = `<phoneme alphabet="${(ph.phoneSet || 'ipa').toLowerCase()}" ph="${ph.phoneme}">${(ph.word || '').trim()}</phoneme>`;
			text = text.replace(new RegExp(ph.word, 'g'), phonemeSsml);
		});
	}

	return text;
}

function getRandomSeed(min = 0, max = 2147483647) {
	return Math.floor(Math.random() * (max - min + 1)) + min;
}

async function generateCreativeProcessPhrases(opts, phrases, resultArray) {
	//Existing insertions preparing
	if (opts.insertions) {
		const exists = opts.insertions.filter(f => ['sfx', 'text'].includes(f.__insertionType));
		await rearrangePhrases(exists);
		resultArray.push(...exists);
		return exists;
	}

	auth.getGlobalProcessId('brief_gnr', true);

	//New insertions generation
	const insertions = [];
	phrases.map(f => {
		if (f.type !== 'text') {
			const item = {
				Oid: uuid(),
				StartTime: 0,
				Duration: 0,
				StartOffset: 0,
				EndOffset: 0,
				Type: 'AudioInsertion',
				__inMemory: true,
				JsonConfig: {
					trackNumber: findTrackNumber(opts, f?.trackNames, f?.trackTemplates),
				},
				Name: f.text,
				__insertionType: 'sfx',
				__adjectives: f.text
			};
			insertions.push(item);
		} else {
			const item = {
				Oid: uuid(),
				StartTime: 0,
				Duration: 0,
				StartOffset: 0,
				EndOffset: 0,
				Type: 'SsmlInsertion',
				Ssml: null,
				Voice: f?.voice?.id || 'en-US-JennyNeural',
				Language: f?.voice?.language?.locale || 'en-US',
				Rate: f?.voice?.supportsTag('prosody', 'rate') ? (f?.voice?.readingRateInfo?.voiceReadingRate || 1) : 1,
				Pitch: 1,
				__generateSsml: () => {
					if (__DEBUG__)
						console.debug('Generate SSML', { item });

					let text = preparePhraseText(f);

					item.Ssml = `
					<speak xmlns="http://www.w3.org/2001/10/synthesis" xmlns:mstts="http://www.w3.org/2001/mstts" xmlns:emo="http://www.w3.org/2009/10/emotionml" version="1.0" xml:lang="${f?.voice?.language?.locale || 'en-US'}">
						<voice name="${f?.voice?.id || 'en-US-JennyNeural'}" xml:lang="${f?.voice?.language?.locale || 'en-US'}">
							<mstts:silence type="Leading-exact" value="40ms"></mstts:silence>
							<mstts:silence type="Sentenceboundary" value="0ms"></mstts:silence>
							<prosody rate="${item.Rate || 1}" pitch="+0%">
								<lang xml:lang="${f?.voice?.language?.locale || 'en-US'}">
									${text}
								</lang>
							</prosody>
							<mstts:silence type="Tailing-exact" value="40ms"></mstts:silence>
						</voice>
					</speak>`
					return item.Ssml;
				},
				__inMemory: true,
				__insertionType: 'text',
				__supportRate: f?.voice?.supportsTag('prosody', 'rate'),
				JsonConfig: {
					trackNumber: findTrackNumber(opts, f?.trackNames, f?.trackTemplates, { addToDucker: true }),
					playbackRate: f?.voice?.supportsTag('prosody', 'rate') ? 1 : f?.voice?.readingRateInfo?.voiceReadingRate
				},
				__singleCall: f.buildInSingleCall,
				__phrase: f,
				VoiceSettings: f.buildInSingleCall ? {
					Stability: 0,
					Similarity: 0,
					Seed: getRandomSeed()
				} : null
			};
			item.__generateSsml();
			insertions.push(item);
		}
	});

	let commonInsertions = Promise.all(insertions.filter(f => !f.__singleCall).map(async f => {
		try {
			const result = f.Type === 'SsmlInsertion' ? await generateSSML(f, { calcDuration: true }) : await findSFX(f?.__adjectives, { ...opts, calcDuration: true });
			if (result?.base64) {
				f.Duration = parseInt(result?.duration / (f.JsonConfig.playbackRate || 1) * 1000) || 0;
				f.__src = result.base64;
				if (f.Type === 'AudioInsertion') {
					//Limit to max 2s
					if (f.Duration > 2000) {
						f.EndOffset = f.Duration - 2000;
					}

					await removeInsertionSilence(f);

					if (__DEBUG__)
						console.debug('Set sfx audio', { f });
				}
			}
		} catch(ex) {
			console.error(ex);
		}
	}));

	let singleCallInsertions = await processSingleCallInsertions(insertions.filter(f => f.__singleCall));

	await Promise.all([commonInsertions, singleCallInsertions]);
	await rearrangePhrases(insertions);

	resultArray.push(...insertions);
	return insertions;
}

async function processSingleCallInsertions(insertions) {
	let groupedByVoice = _.groupBy(insertions, f => f.__phrase?.voice?.id);

	let processGroup = async (voiceId) => {
		let insertions = groupedByVoice[voiceId];
		let voice = insertions[0].__phrase?.voice;
		let insertion = insertions[0];

		let texts = insertions.map(f => preparePhraseText(f.__phrase)).join(' <break time="2s"/>\r\n');

		let aggregatedSSML = `
			<speak xmlns="http://www.w3.org/2001/10/synthesis" xmlns:mstts="http://www.w3.org/2001/mstts" xmlns:emo="http://www.w3.org/2009/10/emotionml" version="1.0" xml:lang="${voice?.language?.locale || 'en-US'}">
				<voice name="${voice?.id || 'en-US-JennyNeural'}" xml:lang="${voice?.language?.locale || 'en-US'}">
					<mstts:silence type="Leading-exact" value="40ms"></mstts:silence>
					<mstts:silence type="Sentenceboundary" value="0ms"></mstts:silence>
					<prosody rate="${voice?.supportsTag('prosody', 'rate') ? (voice?.readingRateInfo?.voiceReadingRate || 1) : 1}" pitch="+0%">
						<lang xml:lang="${voice?.language?.locale || 'en-US'}">
							${texts}
						</lang>
					</prosody>
					<mstts:silence type="Tailing-exact" value="40ms"></mstts:silence>
				</voice>
			</speak>`;

		insertion.Ssml = aggregatedSSML;

		let result = await generateSSMLInsertion(insertion);
		let blob = await Data.dataURItoBlob(result.base64);
		let buffer = await Data.getAudioBufferFromBlob(blob);
		let wordsBoundary = result?.response?.SsmlInsertion?.JsonConfig?.wordsBoundary || [];

		if (__DEBUG__)
			console.debug('processSingleCallInsertions', { wordsBoundary, voice });

		let parts = [];

		let currentPart = {
			start: null,
			end: null
		};
		wordsBoundary.map((f, i) => {
			let lastWord = wordsBoundary[i - 1];

			if (lastWord && (f.ao - lastWord.ao >= 2000)) {
				if (currentPart.end != null) {
					parts.push(currentPart);
				}
				currentPart = {
					start: null,
					end: null
				};
			}

			if (f.ad > 0) {
				if (currentPart.start === null) {
					currentPart.start = f.ao;
				}
				currentPart.end = f.ao + f.ad;
			}
		});

		if (currentPart.end != null) {
			parts.push(currentPart);
		}

		for(let i = 0; i < insertions.length; i++) {
			let insertion = insertions[i];
			let part = parts[i];
			if (part?.end) {				
				let partBuffer = await cut(buffer, part.start / 1000, part.end / 1000);
				let wav = await bufferToWave(partBuffer);
				let base64 = await Data.blob2Base64(wav);
				insertion.__src = base64;
				insertion.Duration = part.end - part.start; 
			} else {
				insertion.__src = result.base64;
				insertion.Duration = parseInt(result?.duration / (insertion.JsonConfig.playbackRate || 1) * 1000) || 0;
			}	
		}
	}

	await Promise.all(Object.keys(groupedByVoice).map(voiceId => processGroup(voiceId)));
}

async function removeInsertionSilence(insertion) {
	const silence = await audioHelper.detectSilence(insertion?.__src, SILENCE_TRIM);
	if (silence?.start) {
		insertion.StartOffset += parseInt(silence?.start * 1000);
		if (insertion.EndOffset) {
			insertion.EndOffset -= Math.max(0, parseInt(silence?.start * 1000));
		}
	}
	if (silence?.end && insertion.EndOffset < silence.end * 1000) {
		insertion.EndOffset = parseInt(silence.end * 1000);
	}

	if (__DEBUG__)
		console.debug('RemoveInsertionSilence', { insertion, silence });
}

async function findMediaAndDownload(adjectives, opts) {
	adjectives = adjectives && Array.isArray(adjectives) ? adjectives?.join(',') : adjectives;
	let mediaFile = opts?.selectedMedia;
	if (!mediaFile) {
		let media = opts?.media || await findMedia(adjectives, opts);
		mediaFile = opts?.randomFunc ? opts?.randomFunc(media) : (opts?.random ? media[_.random(0, media.length-1)] : media[0]);
	}
	
	if (mediaFile) {
		if (__DEBUG__)
			console.debug('Find media and download', { mediaFile });
		const data = await downloadMedia(mediaFile, { onProgress: (percent) => opts?.onProgress && opts?.onProgress(mediaFile?.Id, percent) });
		if (__DEBUG__)
			console.debug('Download media', { mediaFile, data, opts });
		return data;
	}
}

async function findSFX(adjectives, opts) {
	if (adjectives) {
		if (__DEBUG__)
			console.debug('Find sfx', { adjectives, opts });
		const base64 = await findMediaAndDownload(adjectives, { ...(opts || {}), randomFunc: (r) => {
			let items = _.orderBy(r, ['length']);
			if (opts?.aimLength) {
				items = _.orderBy(r, [f => Math.abs(f.length - opts.aimLength)]);
			}
			items = items.slice(0, 5);
			return items[_.random(0, items.length - 1)];
		}, type: 'sound-effects' });
		const res = {
			base64
		}
		
		if (opts?.calcDuration) {
			res.duration = await calcDuration(res.base64);
		}

		if (__DEBUG__)
			console.debug('Find sfx', { res, opts });

		return res;
	}
}

async function generateCreativeProcessMusic(opts, insertions, totalInsertionsDuration) {
	let musicAdjectives = opts?.music?.adjectives || [];
	const startAfterInsertion = opts?.music?.startAfterText ? opts?.phrasesInsertions[opts?.music?.startAfterText - 1] : null;
	const startPosition = opts?.music?.startAfterText > 0 && ((startAfterInsertion?.StartTime + startAfterInsertion?.Duration - startAfterInsertion?.EndOffset - startAfterInsertion?.StartOffset) || 0) / 1000 || 0;
	const endAfterInsertion = opts?.music?.endAfterText ? opts?.phrasesInsertions[opts?.music?.endAfterText - 1] : null;
	const endPosition = opts?.music?.endAfterText > 0 && opts?.music?.endAfterText < insertions.length && ((endAfterInsertion?.StartTime + endAfterInsertion?.Duration - endAfterInsertion?.EndOffset - endAfterInsertion?.StartOffset) || 0) / 1000 || 0;
	let aimLength = ((opts?.aimLength || (totalInsertionsDuration + (opts.musicPosition ? 1 : 2))) - startPosition);

	if (__DEBUG__)
		console.debug('generateCreativeProcessMusic start', { musicAdjectives, aimLength, startPosition, endPosition, opts, totalInsertionsDuration });

	const fade = 50;
	const fadeOut = 0.5;

	//Process existing insertion
	if (opts.insertions) {
		const musicInsertions = opts.insertions.filter(f => f.__insertionType === 'music');
		
		const leftInsertion = musicInsertions[0];
		const rightInsertion = musicInsertions[1];

		const info = leftInsertion.__track().__bpmInfo || await audioHelper.detectBeat(leftInsertion.__track().__buffer, audioCtx);
		const exactLength = await audioHelper.calcExactLength(leftInsertion.__track().__buffer, info, { trimSilence: SILENCE_TRIM, targetLength: aimLength })
		const playbackRate = exactLength.playbackRate;

		leftInsertion.JsonConfig.playbackRate = playbackRate;
		leftInsertion.StartTime = parseInt(startPosition * 1000);
		leftInsertion.StartOffset = parseInt(exactLength.left.startOffset * 1000);
		leftInsertion.EndOffset = parseInt(exactLength.left.endOffset * 1000) - fade;
		
		if (rightInsertion) {
			const leftTrimmedDuration = (leftInsertion.__track().duration - exactLength.left.startOffset - exactLength.left.endOffset) / exactLength.playbackRate;
			rightInsertion.JsonConfig.playbackRate = playbackRate;
			rightInsertion.StartTime = parseInt((startPosition + leftTrimmedDuration) * 1000);
			rightInsertion.StartOffset = parseInt(exactLength.right.startOffset * 1000);
			rightInsertion.EndOffset = parseInt(exactLength.right.endOffset * 1000);
		}

		if (__DEBUG__)
			console.debug('generateCreativeProcessMusic fix', { leftInsertion, rightInsertion });

		return leftInsertion;
	}

	//Generate new
	if (endPosition) {
		aimLength = Math.max(1, endPosition - startPosition + (opts.musicPosition ? 0 : 1));
	}

	if (!musicAdjectives?.length || !aimLength) return;

	if (opts?.state) opts?.state(1);

	let music = await findMediaAndDownload(musicAdjectives, { ...(opts || {}), random: true, aimLength:  Math.max(5, parseInt(aimLength + 1)), media: opts?.music?.media, selectedMedia: opts?.music?.selectedMedia });
	if (music) 
		try {
			const buffer = await audioHelper.getAudioBuffer(music, audioCtx);
			const beatInfo = await audioHelper.detectBeat(buffer, audioCtx);
			const exactLength = await audioHelper.calcExactLength(buffer, beatInfo, { trimSilence: SILENCE_TRIM, targetLength: aimLength, fadeOut })
			const playbackRate = exactLength.playbackRate;

			const leftMusicInsertion = {
				Oid: uuid(),
				Type: 'AudioInsertion',
				StartTime: parseInt(startPosition * 1000),
				__inMemory: true,
				__insertionType: 'music',
				Duration: parseInt(buffer.duration / playbackRate * 1000),
				StartOffset: parseInt(exactLength.left.startOffset * 1000),
				EndOffset: parseInt(exactLength.left.endOffset * 1000) - fade,
				JsonConfig: {
					trackNumber: findTrackNumber(opts, ['music']) || 0,
					fadeOut: {
						value: 0,//fade / 1000,
						type: 'exponential'
					},
					playbackRate
				},
				Name: opts?.music?.selectedMedia?.Name || musicAdjectives.join(', '),
				__src: music
			}
			insertions.push(leftMusicInsertion);
			processRemoveFromDucker(opts, leftMusicInsertion.JsonConfig.trackNumber);

			let rightMusicInsertion = null;

			if (exactLength.right.duration > 0) {
				const leftTrimmedDuration = (buffer.duration / playbackRate) - exactLength.left.startOffset - exactLength.left.endOffset;				
				let startOffset = Math.max(0, leftMusicInsertion.Duration - parseInt(exactLength.right.endOffset * 1000) - fade - parseInt((aimLength - leftTrimmedDuration) * 1000));
				rightMusicInsertion = {
					Oid: uuid(),
					Type: 'AudioInsertion',
					StartTime: parseInt(startPosition * 1000) + parseInt(leftTrimmedDuration * 1000) - fade,
					__inMemory: true,
					__insertionType: 'music',
					Duration: parseInt(buffer.duration / playbackRate * 1000),
					StartOffset: startOffset,
					EndOffset: parseInt(exactLength.right.endOffset * 1000),
					JsonConfig: {
						trackNumber: findTrackNumber(opts, ['music']) || 0,
						fadeIn: {
							value: 0,//fade / 1000,
							type: 'exponential'
						},
						fadeOut: {
							value: fadeOut,
							type: 'exponential'
						},
						playbackRate,
						overlappingMode: 'sourceAudible',
					},
					Name: opts?.music?.selectedMedia?.Name || musicAdjectives.join(', '),
					__src: music
				}
				insertions.push(rightMusicInsertion);
			}

			if (__DEBUG__)
				console.debug('generateCreativeProcessMusic', { opts, leftMusicInsertion, rightMusicInsertion, dur: buffer.duration, exactLength, music, ls: insertions.map(f => f.StartTime + (f.Duration || 0)), startPosition, startAfterInsertion, endAfterInsertion, aimLength, totalInsertionsDuration });
			return leftMusicInsertion;
		} catch(ex) {
			if (__DEBUG__)
				console.error(ex);
		}
}

async function rearrangePhrases(insertions) {
	insertions.reduce((lastStart, ins) => {
		const duration = parseInt(((ins.Duration || 0) - (ins.EndOffset || 0) - (ins.StartOffset || 0)));
		let nextStart = lastStart + duration;

		ins.StartTime = parseInt(lastStart);
		
		//if it sfx - make overlap
		const fadeDelta = 500 ;
		if (ins.__insertionType === 'sfx' && duration >= fadeDelta) {
			const fadeOut = fadeDelta;
			nextStart -= fadeOut;

			ins.JsonConfig.fadeOut = {
				value: fadeOut / 1000,
				type: 'exponential'
			};

			const fadeIn = parseInt(Math.min(Math.max(0, duration - fadeOut - 300), fadeDelta));
			if (ins.StartTime > fadeIn && fadeIn > 0) {
				ins.StartTime -= fadeIn;
				nextStart -= fadeIn;

				ins.JsonConfig.fadeIn = {
					value: fadeIn / 1000,
					type: 'exponential'
				};
			}

			ins.__positionFixes = parseInt(fadeIn + fadeOut);
		}
		return parseInt(nextStart);
	}, 0);

	if (__DEBUG__)
		console.debug('Rearrange phrases', insertions);
}

async function stretchInsertionsToTotalLength(opts, insertions, totalInsertionsDuration, stretchLength) {
	if (stretchLength) {
		const playbackRate = Math.round((totalInsertionsDuration / stretchLength || 1) * 100) / 100;

		if (playbackRate === 1) return;

		//Logic via SSML rate
		await Promise.all(insertions.map(async f => {
			try {
				if (f.Type === 'SsmlInsertion') {
					let newRate = Math.max(0.5, Math.min(2, playbackRate));
					f.Duration = f.Duration / newRate;
					f.JsonConfig.playbackRate = (f.JsonConfig.playbackRate || 1) * newRate;
				}
			} catch(ex) {
				console.error(ex);
			}
		}));

		await rearrangePhrases(insertions);

		if (__DEBUG__)
			console.debug('stretchInsertionsToTotalLength', { playbackRate, insertions, stretchLength, totalInsertionsDuration, opts });
	}
}

function getAggregatedTracks(opts) {
	const aggregatedTracks = [];
	const getTrackNumber = (trackNumber) => {
		if (aggregatedTracks.find(f => f.trackNumber === trackNumber)) return (_.max(_.aggregatedTracks.map(f => f.trackNumber)) || 0) + 1;
		return trackNumber;
	}
	const checkTrack = (track) => {
		if (!aggregatedTracks.find(f => `${f.name}`.toLowerCase() === `${track.name}`.toLowerCase())) {
			track.trackNumber = getTrackNumber(track.trackNumber);
			aggregatedTracks.push(track);
		}
	};

	(opts?.creativeTemplate?.JsonConfig?.aggregatedTracks || []).map(f => checkTrack(f));
	[ 
		{ trackNumber: 1001, name: 'Music', volume: 0.15 },
		{ trackNumber: 1002, name: 'Voice', },
		{ trackNumber: 1003, name: 'SFX', volume: 0.20 },
	].map(f => checkTrack(f));

	return aggregatedTracks;
}

async function generateBackgroundSFX(items, opts) {
	if (__DEBUG__)
		console.debug('Background sfx started', { items, opts });

	if (opts.insertions) {
		const ambientInsertions = opts.insertions.filter(f => f.__insertionType === 'background');

		ambientInsertions.map(ins => {
			const f = ins.__item;
			const startAfterInsertion = f.startAfterText ? opts?.phrasesInsertions[f.startAfterText - 1] : null;
			const startTime = startAfterInsertion ? (startAfterInsertion.StartTime + startAfterInsertion.Duration - startAfterInsertion.EndOffset - startAfterInsertion.StartOffset) : 0;
			const endAfterInsertion = f.endAfterText ? opts?.phrasesInsertions[f.endAfterText - 1] : null;
			const aimLength = endAfterInsertion ? ((endAfterInsertion.StartTime + endAfterInsertion.Duration - endAfterInsertion.EndOffset - endAfterInsertion.StartOffset) - startTime) / 1000 : 0;
			const duration = ins.Duration / 1000;

			let endOffset = 0;
			if (aimLength && aimLength < duration) {
				endOffset = duration - aimLength;
			} else {
				if (duration > 2) {
					endOffset = duration - 2;
				}
			}

			ins.EndOffset = parseInt(endOffset * 1000);
			ins.StartTime = startTime;
		});

		if (__DEBUG__)
			console.debug('Background sfx fixed', { ambientInsertions });

		return ambientInsertions;
	}

	const ambientInsertions = (items || []).map(async f => {
		try {
			if (f.text) {
				const startAfterInsertion = f.startAfterText ? opts?.phrasesInsertions[f.startAfterText - 1] : null;
				const startTime = startAfterInsertion ? (startAfterInsertion.StartTime + startAfterInsertion.Duration - startAfterInsertion.EndOffset - startAfterInsertion.StartOffset) : 0;
				const endAfterInsertion = f.endAfterText ? opts?.phrasesInsertions[f.endAfterText - 1] : null;
				const aimLength = endAfterInsertion ? ((endAfterInsertion.StartTime + endAfterInsertion.Duration - endAfterInsertion.EndOffset - endAfterInsertion.StartOffset) - startTime) / 1000 : 0;
				const sfx = await findSFX(f.text, { ...opts, calcDuration: true, aimLength });

				if (__DEBUG__)
					console.debug(`Background sfx ${f.text}`, { startAfterInsertion, startTime, endAfterInsertion, aimLength });
				
				if (sfx?.base64) {
					let endOffset = 0;
					if (aimLength) {
						if (aimLength < sfx.duration) {
							endOffset = sfx.duration - aimLength;
						}
					} else {
						if (sfx.duration > 2) {
							endOffset = sfx.duration - 2;
						}
					}
					const ambientInsertion = {
						Oid: uuid(),
						Type: 'AudioInsertion',
						StartTime: startTime,
						__inMemory: true,
						__insertionType: 'background',
						Duration: parseInt(sfx?.duration * 1000),
						StartOffset: 0,
						EndOffset: parseInt(endOffset * 1000),
						Name: f.text,
						JsonConfig: {
							trackNumber: findTrackNumber(opts, f.trackNames, f.trackTemplates) || 0,
							fadeOut: {
								value: 0.25,
								type: 'exponential'
							},
							fadeIn: {
								value: 0.25,
								type: 'exponential'
							},
						},
						__item: f
					}

					ambientInsertion.__src = sfx?.base64;
					await removeInsertionSilence(ambientInsertion);
					return ambientInsertion;
				}
			}
		} catch(ex) {
			console.error(ex);
		}
	});

	const result = ambientInsertions?.length ? (await Promise.all(ambientInsertions)).filter(f => f) : [];

	if (__DEBUG__)
		console.debug('Background sfx generated', { ambientInsertions, result });

	return result;
}

function getPureDuration(insertions) {
	return _.max(insertions.map(f => (f.Duration - f.StartOffset - f.EndOffset) + f.StartTime));
}

async function generateCreative(opts) {
	opts = opts || {};
	const phrases = opts?.phrases || [];
	const insertions = [];
	const aggregatedTracks = getAggregatedTracks(opts);

	opts.aggregatedTracks = aggregatedTracks;
	opts.hasMusic = !!opts?.music?.adjectives?.length;
	opts.musicPosition = opts?.music?.startAfterText || 0;
	opts.musicEndPosition = opts?.music?.endAfterText || 0;

	if (__DEBUG__) {
		console.debug('generateCreative start', { opts });
	}
	
	//Voicing SSML phrases
	if (opts?.state) opts?.state(0);
	const phrasesInsertions = await generateCreativeProcessPhrases(opts, phrases, insertions);
	let totalInsertionsDuration = getPureDuration(phrasesInsertions) / 1000;
	opts.phrasesInsertions = phrasesInsertions;

	//Stretch to aim length
	if (opts.aimLength) {
		let stretchLength = opts.hasMusic ? (opts.aimLength - (opts.musicPosition ? (opts.musicEndPosition ? 0 : 1) : (opts.musicEndPosition && opts.musicEndPosition < phrasesInsertions.length ? 1 : 2))) : opts.aimLength;
		await stretchInsertionsToTotalLength(opts, phrasesInsertions, totalInsertionsDuration, stretchLength);
		totalInsertionsDuration = (_.max(insertions.map(f => f.StartTime + ((f.Duration || 0) - (f.EndOffset + f.StartOffset)))) || 4000) / 1000;
	}

	//Process music
	if (opts.hasMusic) {
		await generateCreativeProcessMusic(opts, insertions, totalInsertionsDuration);

		//Add start delay to phrases if music starts from beginning
		if (!opts.musicPosition) {
			phrasesInsertions.map(f => f.StartTime += START_DELAY);
		}
	}
	
	//Process ambience sfx
	if (opts?.ambienceSFX?.length) {
		if (opts?.state) opts?.state(2);
		insertions.push(...await generateBackgroundSFX(opts?.ambienceSFX, opts));
	}

	if (opts?.state) opts?.state(3);
	const creativeOpts = {
		creative: {
			Oid: uuid(),
			JsonConfig: {
				aggregatedTracks,
				creativeVersion: CREATIVE_VERSION,
				masterTrackConfig: opts?.creativeTemplate?.JsonConfig?.masterTrackConfig
			}
		},
		insertions,
		inMemory: true,
		preventKeyHandles: true,
		opts
	}

	//Clear unused tracks
	aggregatedTracks.map(f => f).map(f => {
		if (!insertions.some(i => i.JsonConfig.trackNumber === f.trackNumber)) {
			aggregatedTracks.splice(aggregatedTracks.findIndex(x => x === f), 1);
		}
	});

	if (__DEBUG__)
		console.debug('generateCreative', { creativeOpts, opts, totalInsertionsDuration });
	
	return creativeOpts;
}

export default {
	getFormedResultsRTF,
	generateCreative,
	generateSSML,
	getPureDuration
};

export {
	questionTypes
}
